import type { Bestand } from '../../gedeeld/types'
import { Fout, meldUitgelogd } from './api'

/* ===========================================================================
 *  Bestanden: wat het is, hoe groot, en hoe het naar de server gaat
 *
 *  Twee kanten op. Jij stuurt een PDF, een Excel of een foto mee en Cowork
 *  leest hem; Cowork maakt een Excel of een PDF en jij downloadt hem.
 *
 *  Uploaden gaat NIET via /api/c: het lijf is het bestand zelf, de ruwe
 *  bytes, met de naam en het gesprek in de adresbalk (zie worker/index.ts).
 *  Met XMLHttpRequest en niet met fetch, omdat alleen XHR laat weten hoe
 *  ver hij is -- en bij twaalf megabyte over een telefoonverbinding wil je
 *  een balkje zien en geen rondje dat een halve minuut draait.
 * =========================================================================== */

export const MAX_GROOTTE = 15 * 1024 * 1024

export type BestandSoort = 'pdf' | 'excel' | 'csv' | 'word' | 'powerpoint' | 'beeld' | 'zip' | 'tekst' | 'overig'

/** Op de extensie eerst: een mime-type van de browser is bij Office-bestanden vaak leeg of algemeen. */
export function soortVan(naam: string, mime: string): BestandSoort {
  const ext = naam.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] ?? ''
  const m = mime.toLowerCase()
  if (ext === 'pdf' || m === 'application/pdf') return 'pdf'
  if (ext === 'csv' || m === 'text/csv') return 'csv'
  if (['xlsx', 'xls', 'xlsm', 'ods'].includes(ext) || m.includes('spreadsheet') || m.includes('excel')) return 'excel'
  if (['docx', 'doc', 'odt', 'rtf'].includes(ext) || m.includes('wordprocessing') || m === 'application/msword') return 'word'
  if (['pptx', 'ppt', 'odp'].includes(ext) || m.includes('presentation') || m.includes('powerpoint')) return 'powerpoint'
  if (m.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'bmp', 'tif', 'tiff'].includes(ext)) return 'beeld'
  if (['zip', '7z', 'rar'].includes(ext) || m.includes('zip')) return 'zip'
  if (m.startsWith('text/') || ['txt', 'md', 'json', 'xml', 'log'].includes(ext)) return 'tekst'
  return 'overig'
}

export const SOORT_LABEL: Record<BestandSoort, string> = {
  pdf: 'PDF', excel: 'Excel', csv: 'CSV', word: 'Word', powerpoint: 'PowerPoint',
  beeld: 'Afbeelding', zip: 'ZIP', tekst: 'Tekst', overig: 'Bestand',
}

/** Wat je in de browser kunt bekijken in plaats van downloaden. */
export const kanBekijken = (s: BestandSoort) => s === 'pdf' || s === 'beeld'

/** "24 kB", "3,1 MB": zoals een Nederlandse verkenner het zegt. */
export function grootte(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} kB`
  return `${(n / 1024 / 1024).toLocaleString('nl-NL', { maximumFractionDigits: 1 })} MB`
}

/** Wat Cowork met een upload kan, in een gewone zin. */
export function leesbaarZin(l: Bestand['leesbaar']): string | null {
  switch (l) {
    case 'tekst': return 'Cowork leest de tekst'
    case 'tabel': return 'Cowork leest de tabel'
    case 'beeld': return 'Cowork bekijkt de afbeelding'
    case 'niet': return 'Cowork kan dit type niet lezen'
    default: return null
  }
}

/** Bekijken in een nieuw tabblad (pdf, foto) in plaats van downloaden. */
export const bekijkUrl = (url: string) => `${url}${url.includes('?') ? '&' : '?'}inline=1`

/**
 * Is hij (nog) te downloaden? Een upload wordt nooit bewaard (Casper: "zorg
 * dat je het niet opslaat, nog niet in ieder geval"); een gemaakt bestand
 * tot verlooptOp, een week.
 */
export const isVerlopen = (b: Bestand, nu = Date.now()) => !!b.verlooptOp && b.verlooptOp < nu
export const teDownloaden = (b: Bestand, nu = Date.now()) => !!b.url && b.bewaard && !isVerlopen(b, nu)

/* ------------------------------------------------------------------ *
 *  Voorbeeldplaatjes van wat je zelf toevoegde
 *
 *  Een upload staat niet op de server, dus een plaatje erbij kan alleen
 *  uit het bestand op je eigen computer komen (een object-URL). Die blijft
 *  bestaan zolang dit tabblad open is; daarna is het een icoon.
 * ------------------------------------------------------------------ */

const plaatjes = new Map<string, string>()
export const onthoudPlaatje = (bestandId: string, objectUrl: string) => { plaatjes.set(bestandId, objectUrl) }
export const plaatjeVan = (bestandId: string) => plaatjes.get(bestandId) ?? null

/** De zin bij elke plek waar je iets toevoegt. */
export const NIET_BEWAARD = 'Bijlagen worden niet bewaard. Cowork leest ze alleen voor deze vraag en gooit ze daarna weg.'

/* ------------------------------------------------------------------ *
 *  Uploaden
 * ------------------------------------------------------------------ */

export interface Upload {
  klaar: Promise<Bestand>
  stop: () => void
}

export function upload(bestand: File, gesprekId: string | null, opVoortgang: (deel: number) => void): Upload {
  const xhr = new XMLHttpRequest()
  const klaar = new Promise<Bestand>((goed, fout) => {
    const q = new URLSearchParams({ naam: bestand.name || 'bestand', gesprekId: gesprekId ?? '' })
    xhr.open('POST', `/api/upload?${q}`)
    xhr.setRequestHeader('Content-Type', bestand.type || 'application/octet-stream')
    xhr.setRequestHeader('Accept', 'application/json')
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) opVoortgang(e.loaded / e.total) }
    xhr.onerror = () => fout(new Fout(0, 'Geen verbinding; het bestand kwam niet aan.'))
    xhr.onabort = () => fout(new Fout(0, 'Gestopt.'))
    xhr.onload = () => {
      let lijf: { ok?: boolean; fout?: string; bestand?: Bestand } = {}
      try { lijf = JSON.parse(xhr.responseText) } catch { /* zie hieronder */ }
      if (xhr.status === 401) { meldUitgelogd(); fout(new Fout(401, 'Je bent uitgelogd.')); return }
      if (xhr.status === 413) { fout(new Fout(413, lijf.fout ?? 'Dat bestand is groter dan 15 MB.')); return }
      if (xhr.status < 200 || xhr.status >= 300 || !lijf.ok || !lijf.bestand) {
        fout(new Fout(xhr.status, lijf.fout ?? `Uploaden lukte niet (${xhr.status}).`))
        return
      }
      goed(lijf.bestand)
    }
    xhr.send(bestand)
  })
  return { klaar, stop: () => xhr.abort() }
}
